import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { Printer, Check, Clock, X, Ban } from 'lucide-react';
import { db } from './firebase.js';
import PublicBrandingHeader from './PublicBrandingHeader.jsx';
import PublicBreakdown from './PublicBreakdown.jsx';
import PublicJobPhotos from './PublicJobPhotos.jsx';
import PublicInspectionReport from './PublicInspectionReport.jsx';
import PublicRecommendations from './PublicRecommendations.jsx';
import { grandTotal, REC_ACCEPTED } from './lib/recommendations.js';
import { formatEstimateNumber } from './lib/job-number.js';

function formatPrice(n) {
  if (n == null || isNaN(n)) return '$0.00';
  return `$${Number(n).toFixed(2)}`;
}

function formatDate(ts) {
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function PublicShare() {
  const { shareId } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Local until they submit — nothing is written per tap, so a
  // customer can change their mind as many times as they like.
  const [decisions, setDecisions] = useState({});
  // Declining the whole estimate is a two-tap action. It's terminal
  // and it can't be taken back from this page, so it doesn't get to
  // happen on a mis-tap.
  const [confirmingDecline, setConfirmingDecline] = useState(false);

  // Live subscription so we see "approved" status update if approval happens elsewhere.
  useEffect(() => {
    const ref = doc(db, 'sharedEstimates', shareId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setNotFound(true);
        } else {
          setShare({ id: snap.id, ...snap.data({ serverTimestamps: 'estimate' }) });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Share load failed:', err);
        setError(err.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [shareId]);

  const recommendations = Array.isArray(share?.recommendations) ? share.recommendations : [];
  const hasRecs = recommendations.length > 0;

  // Once submitted, show the customer's own answers back to them
  // rather than the empty local state.
  const shownDecisions = share?.approved
    ? (share.recommendationDecisions || {})
    : decisions;

  const undecidedCount = hasRecs
    ? recommendations.filter((r) => !decisions[r.id]).length
    : 0;

  // The live figure: the agreed work plus whatever they've said yes
  // to so far. An undecided line adds nothing.
  // The quoted work, WITH the tax frozen on at send time. Falls back
  // to the pre-tax total for every estimate sent before tax existed.
  const quotedTotal = Number(share?.grandTotal) || Number(share?.totalPrice) || 0;
  const taxTotal = Number(share?.taxTotal) || 0;

  // Recommendations are a single price with no parts/labor split, so
  // there is no honest way to tax them at two rates here. They are
  // taxed on the invoice, once the accepted work is written up as real
  // parts and labor. The note below says so.
  const runningTotal = grandTotal(
    quotedTotal,
    recommendations.map((r) => ({ ...r, status: shownDecisions[r.id] }))
  );

  function decide(recId, verdict) {
    setDecisions((prev) => {
      const next = { ...prev };
      if (verdict === null) delete next[recId];
      else next[recId] = verdict;
      return next;
    });
  }

  async function handleDecline() {
    if (!share || share.approved || share.declined) return;
    setSubmitting(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'sharedEstimates', shareId), {
        declined: true,
        declinedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error('Decline failed:', e);
      setError('Could not send that — please try again, or call your mechanic directly.');
      setSubmitting(false);
    }
  }

  async function handleApprove() {
    if (!share || share.approved) return;
    setSubmitting(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'sharedEstimates', shareId), {
        approved: true,
        approvedAt: serverTimestamp(),
        // Only sent when there was something to decide, so estimates
        // without recommended work write exactly what they always did.
        ...(hasRecs ? { recommendationDecisions: decisions, decidedTotal: runningTotal } : {}),
      });
      // share state will update via the live subscription
    } catch (e) {
      console.error('Approve failed:', e);
      setError('Could not approve — please try again, or call your mechanic directly.');
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="public-shell">
        <div className="public-card"><p className="muted">Loading…</p></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="public-shell">
        <div className="public-card">
          <h1>Estimate not found</h1>
          <p className="muted">This link may be expired or incorrect. Try contacting your mechanic directly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-shell">
      <div className="public-card">
        <PublicBrandingHeader doc={share} />

        {/* Status pill — sets context the moment the page loads */}
        <div className={`public-status-pill ${share.approved ? 'approved' : 'awaiting'}`}>
          {share.approved
            ? <><Check size={12} strokeWidth={3} /> Approved</>
            : <><Clock size={12} /> Awaiting your approval</>
          }
        </div>

        <p className="public-eyebrow">Estimate for</p>
        <h1>{share.customerName}</h1>
        {/* Blank for estimates sent before numbering shipped. */}
        {formatEstimateNumber(share.jobNumber) && (
          <p className="public-doc-number mono">{formatEstimateNumber(share.jobNumber)}</p>
        )}

        {/* Description block — orange-accent left border instead of just a divider */}
        <div className="public-section-block">
          <p className="public-section-label">What we'll do</p>
          <p className="public-description">{share.description}</p>
        </div>

        <PublicBreakdown share={share} />

        <PublicInspectionReport report={share.inspectionReport} />

        <PublicJobPhotos photos={share.jobPhotos} />

        <PublicRecommendations
          recommendations={recommendations}
          decisions={shownDecisions}
          onDecide={decide}
          locked={share.approved}
        />

        {/* Total — centered, dramatic, set off with top + bottom rule.
            With recommended work on the page this figure moves as the
            customer chooses, so it's labelled to say what it covers. */}
        <div className="public-total-block">
          <p className="public-section-label">
            {hasRecs ? 'Your total' : 'Total'}
          </p>
          <p className="public-total-amount">
            {formatPrice(hasRecs ? runningTotal : quotedTotal)}
          </p>
          {hasRecs && !share.approved && (
            <p className="public-total-note muted small">
              {quotedTotal > 0
                ? `${formatPrice(quotedTotal)} for the work above, plus anything you say yes to.`
                : 'Only what you say yes to.'}
            </p>
          )}
          {hasRecs && taxTotal > 0 && !share.approved && (
            <p className="public-total-note muted small">
              Anything you add is taxed the same way on your final invoice.
            </p>
          )}
        </div>

        {share.declined ? (
          <div className="public-confirmation declined">
            <div className="public-check-mark declined">
              <Ban size={32} strokeWidth={2.5} />
            </div>
            <h2>Thanks for letting us know</h2>
            <p>
              We've told your mechanic you'd rather not go ahead right now. Nothing
              is booked and you won't be charged. Give them a call any time if you
              change your mind.
            </p>
            {share.declinedAt && (
              <p className="public-confirmation-stamp mono">
                {formatDate(share.declinedAt)}
              </p>
            )}
          </div>
        ) : share.approved ? (
          <div className="public-confirmation">
            <div className="public-check-mark">
              <Check size={36} strokeWidth={3} />
            </div>
            <h2>Approved!</h2>
            <p>
              Your mechanic has been notified. They'll be in touch about scheduling.
            </p>
            {share.decidedTotal != null && (
              <p className="public-confirmation-total">
                Approved total: <strong>{formatPrice(share.decidedTotal)}</strong>
              </p>
            )}
            {share.approvedAt && (
              <p className="public-confirmation-stamp mono">
                {formatDate(share.approvedAt)}
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Nudge, not a gate. Someone who only wants the base work
                and ignores every recommendation is giving a valid
                answer, so this never blocks the button. */}
            {hasRecs && undecidedCount > 0 && (
              <p className="public-undecided-note">
                {undecidedCount} recommendation{undecidedCount === 1 ? '' : 's'} still
                unanswered — anything you leave blank is treated as a no.
              </p>
            )}
            <button
              type="button"
              className="public-approve-btn"
              onClick={handleApprove}
              disabled={submitting}
            >
              <Check size={18} strokeWidth={3} />
              {submitting
                ? 'Sending…'
                : (hasRecs ? 'Approve & send my choices' : 'Approve Estimate')}
            </button>
            <p className="public-fineprint">
              Pay your mechanic directly by cash, Venmo, or whatever works — this site
              doesn't handle payment.
            </p>

            {confirmingDecline ? (
              <div className="public-decline-confirm">
                <p>Turn down this estimate? Your mechanic will be told you're not going ahead.</p>
                <div className="public-decline-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirmingDecline(false)}
                  >
                    Never mind
                  </button>
                  <button
                    type="button"
                    className="public-decline-btn"
                    onClick={handleDecline}
                    disabled={submitting}
                  >
                    <X size={15} strokeWidth={3} />
                    {submitting ? 'Sending…' : 'Yes, no thanks'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="public-decline-link"
                onClick={() => setConfirmingDecline(true)}
              >
                No thanks — I'll pass on this
              </button>
            )}
          </>
        )}

        {error && <p className="form-error" style={{ marginTop: 12 }}>{error}</p>}

        <div className="public-print-row no-print">
          <button type="button" className="btn-secondary" onClick={() => window.print()}>
            <Printer size={14} /> Print or Save as PDF
          </button>
        </div>
      </div>

      <footer className="public-footer">
        {(share.businessName || share.businessPhone || share.businessEmail) ? (
          <p className="public-footer-from">
            Estimate from <strong>{share.businessName || 'your mechanic'}</strong>
          </p>
        ) : (
          <p className="public-footer-from">Sent through Bayless</p>
        )}
        <p className="public-attribution">
          <img src="/favicon.png?v=8" alt="" className="public-attribution-mark" />
          Powered by Bayless
        </p>
      </footer>
    </div>
  );
}
